/**
 * ConsolidatedServiceAnalysis Component
 *
 * Sortable table showing per-location service metrics in consolidated view.
 * Displays gross by pay type, controllable expenses %, and RO counts for each location.
 */

import React, { useMemo, useCallback } from 'react';
import {
  WrenchScrewdriverIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  BarsArrowDownIcon,
} from '@heroicons/react/24/outline';
import {
  ConsolidatedServiceLocationData,
  ConsolidatedServiceSortField,
  ConsolidatedServiceSortConfig,
} from '../../types/consolidatedService';

interface ConsolidatedServiceAnalysisProps {
  data: ConsolidatedServiceLocationData[];
  year: number;
  month: number;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  sortConfig: ConsolidatedServiceSortConfig;
  onSortChange: (config: ConsolidatedServiceSortConfig) => void;
}

/** Column definition for sortable headers */
const SORT_COLUMNS: Array<{
  field: ConsolidatedServiceSortField;
  label: string;
  shortLabel: string;
  align: 'left' | 'right';
  format: 'currency' | 'number' | 'percent';
}> = [
  { field: 'locationName', label: 'Location', shortLabel: 'Location', align: 'left', format: 'number' },
  { field: 'totalServiceGross', label: 'Total Gross', shortLabel: 'Total', align: 'right', format: 'currency' },
  { field: 'projectedGross', label: 'Projected', shortLabel: 'Proj.', align: 'right', format: 'currency' },
  { field: 'customer', label: 'Customer', shortLabel: 'Cust.', align: 'right', format: 'currency' },
  { field: 'warranty', label: 'Warranty', shortLabel: 'Warr.', align: 'right', format: 'currency' },
  { field: 'internal', label: 'Internal', shortLabel: 'Int.', align: 'right', format: 'currency' },
  { field: 'other', label: 'Other', shortLabel: 'Other', align: 'right', format: 'currency' },
  { field: 'controllableExpensePct', label: 'Ctrl. Exp. %', shortLabel: 'Ctrl%', align: 'right', format: 'percent' },
  { field: 'closedROCount', label: 'Closed ROs', shortLabel: 'Closed', align: 'right', format: 'number' },
  { field: 'openROCount', label: 'Open ROs', shortLabel: 'Open', align: 'right', format: 'number' },
  { field: 'openRate', label: 'Open Rate %', shortLabel: 'Open%', align: 'right', format: 'percent' },
  { field: 'appointmentsNext5DaysTotal', label: 'Booked / Max (5d)', shortLabel: 'Bkd/Max', align: 'right', format: 'number' },
];

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatPercent(value: number): string {
  return `${value.toFixed(1)}%`;
}

function getMonthName(month: number): string {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  return months[month - 1] || '';
}

/** Get the sort value for a row based on the field */
function getSortValue(row: ConsolidatedServiceLocationData, field: ConsolidatedServiceSortField): string | number {
  switch (field) {
    case 'locationName': return row.locationName;
    case 'totalServiceGross': return row.totalServiceGross;
    case 'projectedGross': return row.projectedGross ?? 0;
    case 'customer': return row.serviceGrossByPayType.customer;
    case 'warranty': return row.serviceGrossByPayType.warranty;
    case 'internal': return row.serviceGrossByPayType.internal;
    case 'other': return row.serviceGrossByPayType.other;
    case 'controllableExpensePct': return row.controllableExpensePct;
    case 'closedROCount': return row.closedROCount;
    case 'openROCount': return row.openROCount;
    case 'openRate': return row.openRate;
    case 'appointmentsNext5DaysTotal': return row.appointmentsNext5DaysTotal ?? 0;
    default: return 0;
  }
}

/** Get the display value for a cell */
function getCellValue(row: ConsolidatedServiceLocationData, field: ConsolidatedServiceSortField): string {
  switch (field) {
    case 'locationName': return row.locationName;
    case 'totalServiceGross': return formatCurrency(row.totalServiceGross);
    case 'projectedGross': return row.projectedGross != null ? formatCurrency(row.projectedGross) : '—';
    case 'customer': return formatCurrency(row.serviceGrossByPayType.customer);
    case 'warranty': return formatCurrency(row.serviceGrossByPayType.warranty);
    case 'internal': return formatCurrency(row.serviceGrossByPayType.internal);
    case 'other': return formatCurrency(row.serviceGrossByPayType.other);
    case 'controllableExpensePct': return formatPercent(row.controllableExpensePct);
    case 'closedROCount': return row.closedROCount.toLocaleString();
    case 'openROCount': return row.openROCount.toLocaleString();
    case 'openRate': return formatPercent(row.openRate);
    case 'appointmentsNext5DaysTotal': {
      const booked = (row.appointmentsNext5DaysTotal ?? 0).toLocaleString();
      const capacity = row.appointmentsNext5DaysCapacityTotal;
      // Hide overlay when capacity is unavailable (e.g. data older than the
      // capacity-overlay deploy, or new locations with no 28-day roster yet).
      if (!capacity) return booked;
      return `${booked} / ${capacity.toLocaleString()}`;
    }
    default: return '';
  }
}

/** Get color class for Total Gross based on whether it meets projected goal */
function getGrossColorClass(row: ConsolidatedServiceLocationData): string {
  if (row.projectedGross == null || row.projectedGross <= 0) return 'text-gray-700';
  return row.totalServiceGross >= row.projectedGross ? 'text-green-600' : 'text-red-600';
}

export const ConsolidatedServiceAnalysis: React.FC<ConsolidatedServiceAnalysisProps> = ({
  data,
  year,
  month,
  collapsed,
  onToggleCollapse,
  sortConfig,
  onSortChange,
}) => {
  // Sort data
  const sortedData = useMemo(() => {
    const sorted = [...data].sort((a, b) => {
      const aVal = getSortValue(a, sortConfig.field);
      const bVal = getSortValue(b, sortConfig.field);

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortConfig.direction === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      const numA = aVal as number;
      const numB = bVal as number;
      return sortConfig.direction === 'asc' ? numA - numB : numB - numA;
    });
    return sorted;
  }, [data, sortConfig]);

  // Calculate totals for footer row
  const totals = useMemo(() => {
    const totalGross = data.reduce((sum, d) => sum + d.totalServiceGross, 0);
    const totalCustomer = data.reduce((sum, d) => sum + d.serviceGrossByPayType.customer, 0);
    const totalWarranty = data.reduce((sum, d) => sum + d.serviceGrossByPayType.warranty, 0);
    const totalInternal = data.reduce((sum, d) => sum + d.serviceGrossByPayType.internal, 0);
    const totalOther = data.reduce((sum, d) => sum + d.serviceGrossByPayType.other, 0);
    const totalCtrlExp = data.reduce((sum, d) => sum + d.totalControllableExpenses, 0);
    const totalProjected = data.reduce((sum, d) => sum + (d.projectedGross ?? 0), 0);
    const hasAnyProjection = data.some(d => d.projectedGross != null);
    const totalClosed = data.reduce((sum, d) => sum + d.closedROCount, 0);
    const totalOpen = data.reduce((sum, d) => sum + d.openROCount, 0);
    const totalAppts5d = data.reduce((sum, d) => sum + (d.appointmentsNext5DaysTotal ?? 0), 0);
    const totalCapacity5d = data.reduce((sum, d) => sum + (d.appointmentsNext5DaysCapacityTotal ?? 0), 0);
    const ctrlExpPct = totalGross !== 0 ? (totalCtrlExp / totalGross) * 100 : 0;
    const openRate = (totalClosed + totalOpen) > 0
      ? (totalOpen / (totalClosed + totalOpen)) * 100
      : 0;

    return {
      totalGross,
      projectedGross: hasAnyProjection ? totalProjected : null,
      customer: totalCustomer,
      warranty: totalWarranty,
      internal: totalInternal,
      other: totalOther,
      ctrlExpPct,
      closedROCount: totalClosed,
      openROCount: totalOpen,
      openRate,
      appointmentsNext5DaysTotal: totalAppts5d,
      appointmentsNext5DaysCapacityTotal: totalCapacity5d,
    };
  }, [data]);

  // Handle sort column click
  const handleSort = useCallback(
    (field: ConsolidatedServiceSortField) => {
      onSortChange({
        field,
        direction:
          sortConfig.field === field && sortConfig.direction === 'desc'
            ? 'asc'
            : 'desc',
      });
    },
    [sortConfig, onSortChange]
  );

  // Render sort indicator
  const renderSortIndicator = (field: ConsolidatedServiceSortField) => {
    if (sortConfig.field !== field) {
      return <BarsArrowDownIcon className="h-3.5 w-3.5 text-gray-300" />;
    }
    return sortConfig.direction === 'asc' ? (
      <ChevronUpIcon className="h-3.5 w-3.5 text-orange-600" />
    ) : (
      <ChevronDownIcon className="h-3.5 w-3.5 text-orange-600" />
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <div
        className={`px-4 py-3 ${collapsed ? '' : 'border-b border-gray-200'} ${onToggleCollapse ? 'cursor-pointer select-none hover:bg-gray-50 transition-colors' : ''}`}
        onClick={onToggleCollapse}
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-orange-100 rounded-lg">
            <WrenchScrewdriverIcon className="h-5 w-5 text-orange-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900">
              Service Department Analysis by Location
            </h3>
            <p className="text-sm text-gray-500">
              {getMonthName(month)} {year} &middot; {data.length} locations
            </p>
          </div>
          {onToggleCollapse && (
            <ChevronDownIcon
              className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${collapsed ? '' : 'rotate-180'}`}
            />
          )}
        </div>
      </div>

      {/* Content - Sortable Table */}
      {!collapsed && (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {SORT_COLUMNS.map((col) => (
                  <th
                    key={col.field}
                    className={`px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:bg-gray-100 transition-colors ${
                      col.align === 'right' ? 'text-right' : 'text-left'
                    }`}
                    onClick={() => handleSort(col.field)}
                  >
                    <div className={`flex items-center gap-1 ${col.align === 'right' ? 'justify-end' : ''}`}>
                      <span className="hidden md:inline">{col.label}</span>
                      <span className="md:hidden">{col.shortLabel}</span>
                      {renderSortIndicator(col.field)}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {sortedData.map((row) => (
                <tr
                  key={row.locationId}
                  className="hover:bg-orange-50 transition-colors"
                >
                  {SORT_COLUMNS.map((col) => {
                    // Determine text color: Total Gross is green/red based on projection
                    let colorClass = col.field === 'locationName' ? 'font-medium text-gray-900' : 'text-gray-700';
                    if (col.field === 'totalServiceGross') {
                      colorClass = `font-semibold ${getGrossColorClass(row)}`;
                    }
                    // Per-day breakdown shown via native title tooltip on the appointments cell
                    const titleAttr =
                      col.field === 'appointmentsNext5DaysTotal' && row.appointmentsNext5Days?.length
                        ? `Booked / Max — ${row.appointmentsNext5Days
                            .map((d) =>
                              d.capacity
                                ? `${d.dayLabel}: ${d.count}/${d.capacity}`
                                : `${d.dayLabel}: ${d.count}`
                            )
                            .join('  •  ')}`
                        : undefined;
                    return (
                      <td
                        key={col.field}
                        title={titleAttr}
                        className={`px-3 py-2.5 text-sm whitespace-nowrap ${
                          col.align === 'right' ? 'text-right' : 'text-left'
                        } ${colorClass} ${titleAttr ? 'cursor-help' : ''}`}
                      >
                        {getCellValue(row, col.field)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
            {/* Footer totals row */}
            <tfoot>
              <tr className="bg-gray-50 border-t-2 border-gray-300">
                <td className="px-3 py-2.5 text-sm font-bold text-gray-900">
                  Total
                </td>
                <td className={`px-3 py-2.5 text-sm font-bold text-right ${
                  totals.projectedGross != null && totals.projectedGross > 0
                    ? (totals.totalGross >= totals.projectedGross ? 'text-green-600' : 'text-red-600')
                    : 'text-gray-900'
                }`}>
                  {formatCurrency(totals.totalGross)}
                </td>
                <td className="px-3 py-2.5 text-sm font-bold text-gray-900 text-right">
                  {totals.projectedGross != null ? formatCurrency(totals.projectedGross) : '—'}
                </td>
                <td className="px-3 py-2.5 text-sm font-bold text-gray-900 text-right">
                  {formatCurrency(totals.customer)}
                </td>
                <td className="px-3 py-2.5 text-sm font-bold text-gray-900 text-right">
                  {formatCurrency(totals.warranty)}
                </td>
                <td className="px-3 py-2.5 text-sm font-bold text-gray-900 text-right">
                  {formatCurrency(totals.internal)}
                </td>
                <td className="px-3 py-2.5 text-sm font-bold text-gray-900 text-right">
                  {formatCurrency(totals.other)}
                </td>
                <td className="px-3 py-2.5 text-sm font-bold text-gray-900 text-right">
                  {formatPercent(totals.ctrlExpPct)}
                </td>
                <td className="px-3 py-2.5 text-sm font-bold text-gray-900 text-right">
                  {totals.closedROCount.toLocaleString()}
                </td>
                <td className="px-3 py-2.5 text-sm font-bold text-gray-900 text-right">
                  {totals.openROCount.toLocaleString()}
                </td>
                <td className="px-3 py-2.5 text-sm font-bold text-gray-900 text-right">
                  {formatPercent(totals.openRate)}
                </td>
                <td className="px-3 py-2.5 text-sm font-bold text-gray-900 text-right">
                  {totals.appointmentsNext5DaysCapacityTotal > 0
                    ? `${totals.appointmentsNext5DaysTotal.toLocaleString()} / ${totals.appointmentsNext5DaysCapacityTotal.toLocaleString()}`
                    : totals.appointmentsNext5DaysTotal.toLocaleString()}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
};
